import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Tag,
  Space,
  MessagePlugin,
  DateRangePicker,
} from 'tdesign-react';
import { SearchIcon, RefreshIcon } from 'tdesign-icons-react';
import { visitLogApi } from '../../api';
import type { VisitLog } from '../../types';

export default function VisitLogManage() {
  const [data, setData] = useState<VisitLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchForm, setSearchForm] = useState({
    ip: '',
    username: '',
    url: '',
    dateRange: [] as [string, string] | null,
  });

  const columns = [
    { colKey: 'username', title: '用户', width: 120,
      cell: ({ row }: any) => (
        <Tag theme={row.username ? 'success' : row.username === '' ? 'danger' : 'warning'}>
          {row.username || '-'}
        </Tag>
      ),
     },
    { colKey: 'ip', title: 'IP 地址', width: 160 },
    {
      colKey: 'requestMethod',
      title: '请求方法',
      width: 100,
      cell: ({ row }: any) => {
        const methodColors: Record<string, string> = {
          GET: 'success',
          POST: 'primary',
          PUT: 'warning',
          DELETE: 'danger',
        };
        return <Tag theme={(methodColors[row.requestMethod] || 'default') as any}>{row.requestMethod || '-'}</Tag>;
      },
    },
    { colKey: 'url', title: '请求地址', ellipsis: true },
    {
      colKey: 'responseStatus',
      title: '状态码',
      width: 100,
      cell: ({ row }: any) => (
        <Tag theme={row.responseStatus === 200 ? 'success' : row.responseStatus >= 400 ? 'danger' : 'warning'}>
          {row.responseStatus || '-'}
        </Tag>
      ),
    },
    {
      colKey: 'responseTime',
      title: '响应时间',
      width: 120,
      cell: ({ row }: any) => (
        <span style={{ color: row.costTime > 1000 ? '#F53F3F' : row.costTime > 500 ? '#FF7D00' : '#00B42A' }}>
          {row.costTime != null ? `${row.costTime}ms` : '-'}
        </span>
      ),
    },
    { colKey: 'accessTime', title: '访问时间', width: 180 },
  ];

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const params: any = { pageNum: page, pageSize };
      if (searchForm.ip) params.ip = searchForm.ip;
      if (searchForm.username) params.username = searchForm.username;
      if (searchForm.url) params.url = searchForm.url;
      if (searchForm.dateRange && searchForm.dateRange.length === 2) {
        params.startTime = searchForm.dateRange[0];
        params.endTime = searchForm.dateRange[1];
      }

      const res = await visitLogApi.getList(params);
      if (res.code === 200) {
        setData(res.data.list || res.data.records || []);
        setPagination({
          current: res.data.pageNum || res.data.current || page,
          pageSize: res.data.pageSize || res.data.size || pageSize,
          total: res.data.total,
        });
      }
    } catch {
      MessagePlugin.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchData(1, pagination.pageSize);
  };

  const handleReset = () => {
    setSearchForm({ ip: '', username: '', url: '', dateRange: null });
    setPagination({ ...pagination, current: 1 });
    fetchData(1, pagination.pageSize);
  };

  const handlePageChange = (pageInfo: any) => {
    setPagination({ ...pagination, current: pageInfo.current, pageSize: pageInfo.pageSize });
    fetchData(pageInfo.current, pageInfo.pageSize);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">访客日志</h1>

      {/* 搜索栏 */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
        <Space>
          <Input
            placeholder="请输入 IP 地址"
            prefixIcon={<SearchIcon />}
            value={searchForm.ip}
            onChange={(val: string) => setSearchForm({ ...searchForm, ip: val })}
            style={{ width: 200 }}
          />
          <Input
            placeholder="请输入用户名"
            value={searchForm.username}
            onChange={(val: string) => setSearchForm({ ...searchForm, username: val })}
            style={{ width: 160 }}
          />
          <Input
            placeholder="请输入请求地址"
            value={searchForm.url}
            onChange={(val: string) => setSearchForm({ ...searchForm, url: val })}
            style={{ width: 200 }}
          />
          <DateRangePicker
            placeholder={['开始时间', '结束时间']}
            value={searchForm.dateRange || []}
            onChange={(val: any) => setSearchForm({ ...searchForm, dateRange: val || null })}
            style={{ width: 280 }}
          />
          <Button theme="primary" icon={<SearchIcon />} onClick={handleSearch}>
            查询
          </Button>
          <Button icon={<RefreshIcon />} onClick={handleReset}>
            重置
          </Button>
        </Space>
      </div>

      {/* 数据表格 */}
      <Table
        data={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          onChange: handlePageChange,
        }}
        bordered
        hover
        stripe
      />
    </div>
  );
}
